import React, { useState } from 'react';
import { Input, Button, Pill, Text } from '@clockout-dev/design-elements';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import ImportListModal from './ImportListModal';
import styles from '../styles/userEligibilityForm.module.scss';

/**
 * Member block list editor. Manages DENY overrides for blocking specific members.
 * Calls handlers that manage API calls and state updates.
 *
 * `readOnly` hides every write control (Import, the add row, per-row
 * unblock) rather than merely disabling them — the session either lacks
 * admin:access_list:manage entirely or is view-only on Configurations, and
 * the underlying handlers are themselves guarded, so a hidden control here
 * is defense in depth, not the only thing standing between this session and
 * a write.
 */
function BlockedEditor({ blockedMembers = [], onAdd, onRemove, onBulkSuccess, onBulkUpload, onPollBulkStatus, readOnly = false }) {
    const [draft, setDraft] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const submit = () => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        if (blockedMembers.some(m => m.member_id === trimmed)) return;

        onAdd?.(trimmed);
        setDraft('');
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submit();
        }
    };

    const isDuplicate = draft.trim() !== '' && blockedMembers.some(m => m.member_id === draft.trim());

    return (
        <div className={styles.blockedEditor}>
            <div className={styles.editorHeader}>
                <Text as="span" size="xs" className={styles.blockedDescription}>
                    Add member IDs to deny access to the service.
                </Text>
                {!readOnly ? (
                    <Button
                        type="button"
                        size="s"
                        onClick={() => setIsModalOpen(true)}
                        className={styles.importButton}
                    >
                        <DownloadOutlined />
                        Import List
                    </Button>
                ) : null}
            </div>
            {!readOnly ? (
                <div className={styles.blockedInputRow}>
                    <Input
                        id="blockedMemberId"
                        size="large"
                        label="Member ID"
                        labelSize="xs"
                        placeholder="Enter member ID..."
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={handleKeyDown}
                        classNames={{ root: styles.blockedInput }}
                    />
                    <Button
                        type="button"
                        size="m"
                        onClick={submit}
                        disabled={!draft.trim() || isDuplicate}
                        className={styles.blockedAddButton}
                    >
                        Block
                    </Button>
                </div>
            ) : null}

            {!readOnly ? (
                <ImportListModal
                    mode="blocked"
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={(summary) => {
                        setIsModalOpen(false);
                        onBulkSuccess?.(summary);
                    }}
                    onUpload={onBulkUpload}
                    onPollStatus={onPollBulkStatus}
                />
            ) : null}
            {blockedMembers.length > 0 ? (
                <div className={styles.blockedMembers}>
                    <Text as="span" size="xs" className={styles.blockedCount}>
                        {`Blocked Members (${blockedMembers.length})`}
                    </Text>
                    <ul className={styles.blockedList}>
                        {blockedMembers.map((member) => (
                            <li key={member.id || member.member_id} className={styles.blockedItem}>
                                <Pill
                                    color="#ffe6e6"
                                    text={member.member_id}
                                    textSize="xs"
                                    classNames={{ root: styles.blockedPill, text: styles.blockedPillText }}
                                />
                                {!readOnly ? (
                                    <button
                                        type="button"
                                        aria-label={`Unblock ${member.member_id}`}
                                        className={styles.blockedRemoveButton}
                                        onClick={() => onRemove?.(member.member_id, member.id)}
                                    >
                                        <CloseOutlined style={{ fontSize: 11 }} />
                                    </button>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <Text as="span" size="xs" className={styles.blockedEmpty}>
                    No members blocked yet.
                </Text>
            )}
        </div>
    );
}

export default BlockedEditor;
